import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  TEAM_CHAT_BODY_MAX,
  TEAM_ROOM_ID,
  bellRecipientIds,
  channelLabel,
  conversationTitle,
  directMessageId,
  previewText,
  type TeamChatKind,
  type TeamChatMemberRef,
} from './teamChat';

export interface TeamConversation {
  id: string;
  kind: TeamChatKind;
  title: string;
  memberIds: string[];
  createdBy: string;
  lastMessageAt: Date | null;
  lastMessageText: string;
  lastMessageAuthorId: string;
  lastMessageAuthorName: string;
}

export interface TeamChatMessage {
  id: string;
  authorId: string;
  authorName: string;
  body: string;
  createdAt: Date | null;
}

export interface TeamChatRead {
  lastReadAt: Date | null;
}

function asDate(value: unknown): Date | null {
  if (value && typeof (value as Timestamp).toDate === 'function') {
    return (value as Timestamp).toDate();
  }
  return null;
}

function asKind(value: unknown): TeamChatKind {
  return value === 'dm' || value === 'group' || value === 'team' ? value : 'group';
}

function mapConversation(id: string, data: Record<string, unknown>): TeamConversation {
  const memberIds = Array.isArray(data.memberIds)
    ? data.memberIds.filter((id): id is string => typeof id === 'string' && id.length > 0)
    : [];
  return {
    id,
    kind: asKind(data.kind),
    title: typeof data.title === 'string' ? data.title : '',
    memberIds,
    createdBy: typeof data.createdBy === 'string' ? data.createdBy : '',
    lastMessageAt: asDate(data.lastMessageAt),
    lastMessageText: typeof data.lastMessageText === 'string' ? data.lastMessageText : '',
    lastMessageAuthorId: typeof data.lastMessageAuthorId === 'string' ? data.lastMessageAuthorId : '',
    lastMessageAuthorName: typeof data.lastMessageAuthorName === 'string' ? data.lastMessageAuthorName : '',
  };
}

export function subscribeTeamConversations(
  uid: string,
  onChange: (rows: TeamConversation[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const q = query(collection(db, 'teamConversations'), where('memberIds', 'array-contains', uid));
  return onSnapshot(q, (snap) => {
    const rows = snap.docs.map((d) => mapConversation(d.id, d.data() as Record<string, unknown>));
    rows.sort((a, b) => (b.lastMessageAt?.getTime() || 0) - (a.lastMessageAt?.getTime() || 0));
    onChange(rows);
  }, (err) => onError?.(err));
}

export function subscribeTeamMessages(
  conversationId: string,
  onChange: (rows: TeamChatMessage[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const q = query(
    collection(db, 'teamConversations', conversationId, 'messages'),
    orderBy('createdAt', 'asc'),
  );
  return onSnapshot(q, (snap) => {
    onChange(snap.docs.map((d) => {
      const data = d.data() as Record<string, unknown>;
      return {
        id: d.id,
        authorId: typeof data.authorId === 'string' ? data.authorId : '',
        authorName: typeof data.authorName === 'string' ? data.authorName : '',
        body: typeof data.body === 'string' ? data.body : '',
        createdAt: asDate(data.createdAt),
      };
    }));
  }, (err) => onError?.(err));
}

export function subscribeTeamRead(
  conversationId: string,
  uid: string,
  onChange: (read: TeamChatRead) => void,
): () => void {
  return onSnapshot(doc(db, 'teamConversations', conversationId, 'reads', uid), (snap) => {
    const data = snap.data() as Record<string, unknown> | undefined;
    onChange({ lastReadAt: asDate(data?.lastReadAt) });
  }, () => onChange({ lastReadAt: null }));
}

export async function ensureTeamRoom(uid: string): Promise<void> {
  const ref = doc(db, 'teamConversations', TEAM_ROOM_ID);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      tx.set(ref, {
        kind: 'team',
        title: 'team',
        memberIds: [uid],
        createdBy: uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return;
    }
    const members = Array.isArray(snap.data().memberIds) ? snap.data().memberIds as string[] : [];
    if (!members.includes(uid)) {
      tx.update(ref, {
        memberIds: arrayUnion(uid),
        updatedAt: serverTimestamp(),
      });
    }
  });
}

export async function openOrCreateDirectMessage(selfId: string, otherId: string): Promise<string> {
  const id = directMessageId(selfId, otherId);
  const ref = doc(db, 'teamConversations', id);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (snap.exists()) return;
    tx.set(ref, {
      kind: 'dm',
      title: '',
      memberIds: [selfId, otherId].sort(),
      createdBy: selfId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  });
  return id;
}

export async function createGroupChannel(input: {
  selfId: string;
  title: string;
  memberIds: string[];
}): Promise<string> {
  const title = input.title.trim().replace(/^#/, '');
  const memberIds = Array.from(new Set([input.selfId, ...input.memberIds])).filter(Boolean);
  const ref = doc(collection(db, 'teamConversations'));
  await setDoc(ref, {
    kind: 'group',
    title,
    memberIds,
    createdBy: input.selfId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function markTeamConversationRead(conversationId: string, uid: string): Promise<void> {
  await setDoc(doc(db, 'teamConversations', conversationId, 'reads', uid), {
    lastReadAt: serverTimestamp(),
  }, { merge: true });
}

export async function sendTeamChatMessage(input: {
  conversationId: string;
  kind: TeamChatKind;
  title: string;
  memberIds: string[];
  authorId: string;
  authorName: string;
  body: string;
  members: TeamChatMemberRef[];
}): Promise<void> {
  const body = input.body.trim().slice(0, TEAM_CHAT_BODY_MAX);
  if (!body) return;
  const messageRef = doc(collection(db, 'teamConversations', input.conversationId, 'messages'));
  await setDoc(messageRef, {
    authorId: input.authorId,
    authorName: input.authorName,
    body,
    createdAt: serverTimestamp(),
  });
  await updateDoc(doc(db, 'teamConversations', input.conversationId), {
    lastMessageAt: serverTimestamp(),
    lastMessageText: previewText(body),
    lastMessageAuthorId: input.authorId,
    lastMessageAuthorName: input.authorName,
    updatedAt: serverTimestamp(),
  });
  await markTeamConversationRead(input.conversationId, input.authorId);

  const recipients = bellRecipientIds({
    kind: input.kind,
    memberIds: input.memberIds,
    authorId: input.authorId,
    body,
    members: input.members,
  });
  const label = channelLabel(
    input.kind,
    conversationTitle(input.kind, input.title, input.memberIds, input.authorId, (uid) => (
      input.members.find((m) => m.uid === uid)?.displayName || 'someone'
    )),
  );
  await Promise.all(recipients.map((userId) => addDoc(collection(db, 'userNotifications'), {
    userId,
    type: 'team_chat',
    title: input.kind === 'team' ? `${input.authorName} mentioned you` : `New message from ${input.authorName}`,
    message: `${label}: ${previewText(body)}`,
    link: `/team-chat?c=${encodeURIComponent(input.conversationId)}`,
    sourceId: input.conversationId,
    read: false,
    isPriority: false,
    createdAt: serverTimestamp(),
  })));
}
